import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box, Button, MenuItem, Select, SelectChangeEvent,
} from '@mui/material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { OrderAttrFilter } from 'src/components/dispatch/orderAttrFilter';
import { DisplayMetric } from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';
import {
  UnassignedDisplayModeSection,
} from 'src/components/dispatch/unassignedDisplay/UnassignedDisplayModeSection';
import { displayMetricScopeOf } from 'src/components/dispatch/unassignedDisplay/useDisplayMetrics';
import { UnassignedDisplayMode } from 'src/components/dispatch/unassignedDisplay/useUnassignedDisplayMode';
import { ShipmentOrderForm } from 'src/components/V2ShipmentOrderForm';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import DisplayMetricToggle from 'src/pages/ConvenienceDispatches/DisplayMetricToggle';
import { resolveDateRangeChange } from 'src/pages/Map/resolveDateRangeChange';
import { buildTruckShiftsPath } from 'src/pages/TruckShifts/truckShiftGrid';

import { CellColorDialog } from './CellColorDialog';
import { CellColorSection } from './CellColorSection';
import { CellColorArea, DispatchCellColorSettings } from './resolveCellColor';
import {
  endOnForSpan, isDisabledRangeDate, LONG_HAUL_SPAN_OPTIONS, spanDaysOf,
} from './spanDays';

/**
 * 'YYYY-MM-DD' → Date（ローカル 0 時）。読めなければ null。
 *
 * Why not new Date(str): 'YYYY-MM-DD' は UTC 解釈されるので、西側のタイムゾーンでは
 * 前日になる（`/map` の MapToolbar はここを `new Date(str)` のままにしているが、
 * 写すにあたって displayDays.ts / spanDays.ts と同じ数値分解に揃える）。
 */
const parseDay = (day: string): Date | null => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!matched) return null;
  const date = new Date(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

type Props = {
  /** 画面遷移。Container の useNavigate を包んで渡す */
  onNavigate: (path: string) => void;
  /** 日付範囲ピッカーの現在値。URL パラメータ由来 */
  startOn: string;
  endOn: string;
  /**
   * 日付が確定したときに呼ぶ。`/long-haul` は日付を URL に持つので、呼び出し側は
   * `navigate('/long-haul/{startOn}/{endOn}')` する（`/map`・`/plans` と同じ方式）。
   * 判定（逆転・10 日制限）は `resolveDateRangeChange` が済ませてある。
   */
  onDateRangeChange: (startOn: string, endOn: string) => void;
  /** 自動配車ボタン。props 名は `/map`（MapToolbar.tsx）と揃える */
  onAutoDispatch: () => void;
  isAutoDispatchDisabled: boolean;
  autoDispatchTooltip?: string;
  /** 表示項目トグル。画面内で **1 箇所にだけ**置く（右カラム側に復活させないこと） */
  displayMetrics: DisplayMetric[];
  onDisplayMetricsChange: (value: DisplayMetric[]) => void;
  customInputFieldNames: string[];
  isLicenseReady: boolean;
  /** 表示対象（配送形態・区間種別）の絞り込み。表示項目トグルの中に置く */
  orderAttrFilter: OrderAttrFilter;
  onOrderAttrFilterChange: (next: OrderAttrFilter) => void;
  /**
   * セルの色分け (log_web_issues#338 C2)。表示項目トグルの中に置く。
   * API の PUT が設定全体の置き換えなので、変更も設定全体で受け渡す。
   */
  cellColorSettings: DispatchCellColorSettings;
  /** エリアと所属県。API が返す対応表をそのまま色設定ダイアログへ渡す */
  cellColorAreas: readonly CellColorArea[];
  onCellColorSettingsChange: (next: DispatchCellColorSettings) => void;
  /**
   * 未割当リストの表示単位 (log_web_issues#338 C8)。表示項目トグルの中に置く。
   * 値を持つのは Presenter（同じ値を右カラムの未割当リストへも渡すため）。
   */
  unassignedDisplayMode: UnassignedDisplayMode;
  onUnassignedDisplayModeChange: (next: UnassignedDisplayMode) => void;
};

/**
 * 長距離配車の横断ツールバー。`/map` の MapToolbar.tsx を写して作った。
 *
 * flexWrap で折り返す。**個々のボタンを隠さない**（隠すと機能に到達できなくなる）。
 * 高さは内容なりに伸びるので、親は flex: 0 0 auto で受けること。
 *
 * Why not MapToolbar を共通化して使い回す: あちらはシナリオ配車を抱えており、
 * こちらは表示日数を持つ。差分を props の出し分けで畳むと、どちらの画面に何が出るのかが
 * 1 つのコンポーネントの分岐を読まないと分からなくなる（`/map` は触らない、という
 * 本タスクの制約にも合わせている）。
 */
const LongHaulToolbar: FC<Props> = memo(({
  onNavigate,
  startOn,
  endOn,
  onDateRangeChange,
  onAutoDispatch,
  isAutoDispatchDisabled,
  autoDispatchTooltip,
  displayMetrics,
  onDisplayMetricsChange,
  orderAttrFilter,
  onOrderAttrFilterChange,
  customInputFieldNames,
  isLicenseReady,
  cellColorSettings,
  cellColorAreas,
  onCellColorSettingsChange,
  unassignedDisplayMode,
  onUnassignedDisplayModeChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // 案件作成ダイアログの開閉はここで完結し外へ出す理由が無いので、この 1 つだけ state を持つ
  // （MapToolbar.tsx:88 と同じ）
  const [newShipmentDialogOpen, setNewShipmentDialogOpen] = useState(false);

  // 色を変更するダイアログ。表示項目の Popover の外に置く（中に置くと、ダイアログを
  // 押した時点で Popover が「外を押した」と見て閉じ、子ごとダイアログが消える）
  const [cellColorDialogOpen, setCellColorDialogOpen] = useState(false);

  // ピッカーは片方だけ選ばれた中間状態を持つので、確定値（props）とは別に保持する
  const [selectedRange, setSelectedRange] = useState<DateRange<Date>>([
    parseDay(startOn), parseDay(endOn),
  ]);

  useEffect(() => {
    setSelectedRange([parseDay(startOn), parseDay(endOn)]);
  }, [startOn, endOn]);

  const handleDateRange = useCallback((range: DateRange<Date>) => {
    // 片方だけ選んだ時点でも表示は追随させる（確定は resolve が判断する）
    setSelectedRange((prev) => [range[0] ?? prev[0], range[1] ?? prev[1]]);

    const result = resolveDateRangeChange([range[0] ?? null, range[1] ?? null]);
    if (result.kind === 'ignore') return;
    if (result.kind === 'reject') {
      enqueueSnackbar(result.message);
      return;
    }

    ReactGA.event('select', {
      screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
      button_name: '日付範囲',
      label: `${result.startOn}~${result.endOn}`,
    });
    onDateRangeChange(result.startOn, result.endOn);
  }, [enqueueSnackbar, onDateRangeChange]);

  /**
   * 表示日数の選択状態。現在の期間から逆算する。
   * 1/3/7 日のいずれでもない期間（URL 直打ち等）では null = どれも選ばれていない。
   */
  const selectedSpanDays = useMemo(() => {
    const days = spanDaysOf(startOn, endOn);
    return LONG_HAUL_SPAN_OPTIONS.some((option) => option.days === days) ? days : null;
  }, [startOn, endOn]);

  /**
   * 表示項目の保存を分ける区分の添え字 (log_web_issues#338 C7)。
   * 選択状態 (selectedSpanDays) と違い、1/3/7 日以外の期間でも必ずどれかに落ちる
   * （保存先が無い期間を作らないため）。
   */
  const metricScopeLabel = useMemo(() => {
    const scope = displayMetricScopeOf(spanDaysOf(startOn, endOn));
    return scope === 'week' ? '週' : (scope === '3d' ? '3日' : '1日');
  }, [startOn, endOn]);

  /**
   * 表示日数の変更。開始日は据え置き、終了日だけ動かす。
   *
   * Why not onDateRangeChange に流す: あちらは「ピッカーで選んだ 2 日」を
   * `resolveDateRangeChange` で検査してから通す経路。こちらは選択肢が固定で
   * 逆転も 10 日超過も起こりえないので、検査を挟まず URL をそのまま書き換える。
   */
  const handleSpanChange = useCallback((days: number) => {
    if (!startOn) return;
    ReactGA.event('click', {
      screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
      button_name: '表示日数',
      label: `${days}`,
    });
    onNavigate(`/long-haul/${startOn}/${endOnForSpan(startOn, days)}`);
  }, [onNavigate, startOn]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        bgcolor: '#fff',
        borderBottom: '1px solid hsl(var(--border))',
      }}
    >
      {/* 左寄せ: 日付・表示日数・稼働表 */}
      <DateRangePicker
        value={selectedRange}
        onOpen={() => ReactGA.event('click', {
          screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
          button_name: '日付選択',
        })}
        onChange={handleDateRange}
        // 終了日は 3 日 / 7 日の 2 つだけ押せる。押せない日をカレンダーで灰色にして、
        // 選ぶ前に分かるようにする（判定は spanDays.ts の純粋関数）
        shouldDisableDate={(day, position) => isDisabledRangeDate(day, position, selectedRange[0])}
        slotProps={{ textField: { size: 'small', sx: { p: 0, m: 0 } } }}
      />

      {/* 表示日数。サンプル（route-planner の PlanToolbar.tsx）と同じセレクト 1 つの形。
          高さ 28・fontSize 12・minWidth 84 もあちらから写している。
          空文字は「1/3/7 日のいずれでもない期間」。MUI は '' だけを out-of-range 警告の
          対象外にしているので、どれも選ばれていない状態はこの値で表す */}
      <Select
        size="small"
        value={selectedSpanDays ?? ''}
        onChange={(event: SelectChangeEvent<number | ''>) => {
          const next = event.target.value;
          if (typeof next === 'number') handleSpanChange(next);
        }}
        inputProps={{ 'aria-label': '表示日数' }}
        sx={{
          flexShrink: 0,
          height: 28,
          fontSize: 12,
          minWidth: 84,
          '& .MuiSelect-select': { py: 0, pl: 1.25, pr: '24px !important' },
        }}
      >
        {LONG_HAUL_SPAN_OPTIONS.map((option) => (
          <MenuItem key={option.days} value={option.days} sx={{ fontSize: 12 }}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      {/* 稼働表 (/truck-shifts)。backTo に 'long-haul' を渡すので、稼働表の「戻る」で
          同じ期間の /long-haul へ戻れる（truckShiftGrid.ts の backDestinationPath に
          long-haul の分岐を足してある）。 */}
      <Button
        size="small"
        onClick={() => {
          if (!startOn) return;
          ReactGA.event('click', {
            screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
            button_name: '稼働表',
          });
          onNavigate(buildTruckShiftsPath({ startOn, endOn: endOn || startOn, backTo: 'long-haul' }));
        }}
        disabled={!startOn}
        sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
      >
        稼働表
      </Button>

      {/* 左右を分ける。以降はすべて右寄せ */}
      <Box sx={{ flex: 1 }} />

      <Button
        size="small"
        startIcon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
        onClick={() => {
          ReactGA.event('click', {
            screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
            button_name: '自動配車',
          });
          onAutoDispatch();
        }}
        disabled={isAutoDispatchDisabled}
        title={autoDispatchTooltip}
        // 押せる状態を色で示す。無効時まで contained にすると、押せないのに
        // 主要操作に見える
        variant={isAutoDispatchDisabled ? 'text' : 'contained'}
        sx={{
          textTransform: 'none',
          fontSize: 12,
          ...(isAutoDispatchDisabled ? { color: 'text.secondary' } : {}),
        }}
      >
        自動配車
      </Button>

      <Button
        size="small"
        startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
        onClick={() => {
          ReactGA.event('click', {
            screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
            button_name: 'インポート',
          });
          onNavigate('/import');
        }}
        sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
      >
        インポート
      </Button>

      {/* 案件作成（V3Plans・カード配車・/map と同じ ShipmentOrderForm ダイアログ） */}
      <Button
        size="small"
        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
        onClick={() => {
          if (!startOn) return;
          ReactGA.event('click', {
            screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
            button_name: '案件作成',
          });
          setNewShipmentDialogOpen(true);
        }}
        disabled={!startOn}
        sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
      >
        案件作成
      </Button>

      {/* startOn が無いまま開くと baseDate が Invalid Date になる
          （MapToolbar.tsx:254 の `startOn &&` を写したもの） */}
      {startOn && newShipmentDialogOpen && (
        <ShipmentOrderForm
          baseDate={parseDay(startOn) ?? new Date()}
          startOn={startOn}
          newShipmentDialogOpen={newShipmentDialogOpen}
          setNewShipmentDialogOpen={setNewShipmentDialogOpen}
        />
      )}

      {/* 表示項目（右寄せの末尾）。画面内で 1 箇所だけ */}
      <DisplayMetricToggle
        value={displayMetrics}
        onChange={onDisplayMetricsChange}
        orderAttrFilter={orderAttrFilter}
        onOrderAttrFilterChange={onOrderAttrFilterChange}
        customInputFieldNames={customInputFieldNames}
        isLicenseReady={isLicenseReady}
        screenName={SCREEN_NAMES.PLANNING_LONG_HAUL}
        scopeLabel={metricScopeLabel}
        // 積込・納品タグと粗利は左カラムごと隠している（LONG_HAUL_HIDDEN_DAILY_METRICS）ので、
        // 車両リスト表示タグのセクションは出さない（ON にしても何も起きない）
        showVehicleMetrics={false}
        // セルの色分け → 未割当リスト表示の順（サンプル
        // route-planner MatchingPresenter/index.tsx:1399-1485 と同じ並び）
        extras={(
          <>
            <CellColorSection
              settings={cellColorSettings}
              onChange={onCellColorSettingsChange}
              onOpenColorDialog={() => {
                ReactGA.event('click', {
                  screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
                  button_name: 'セル色分け 色設定',
                });
                setCellColorDialogOpen(true);
              }}
            />
            <UnassignedDisplayModeSection
              value={unassignedDisplayMode}
              onChange={onUnassignedDisplayModeChange}
              screenName={SCREEN_NAMES.PLANNING_LONG_HAUL}
            />
          </>
        )}
      />

      {cellColorDialogOpen && (
        <CellColorDialog
          open
          settings={cellColorSettings}
          areas={cellColorAreas}
          onClose={() => setCellColorDialogOpen(false)}
          onSave={onCellColorSettingsChange}
        />
      )}
    </Box>
  );
});

export default LongHaulToolbar;
